// ============================================================================
// PARTY + SALES master endpoints.
// ============================================================================

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

const PARTY_BASE = '/parties';
export const partyEndpoints = {
  list: () => buildEndpoint(PARTY_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${PARTY_BASE}/:id`, { id }),
  create: () => buildEndpoint(PARTY_BASE),
  update: (id: ID) => buildEndpointWithParams(`${PARTY_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${PARTY_BASE}/:id`, { id }),
  validateCode: (code: string) => buildEndpointWithParams(`${PARTY_BASE}/validate/code/:code`, { code }),
} as const;

const RECOVERABLE_EXPENSE_BASE = '/recoverable-expenses';
export const recoverableExpenseEndpoints = {
  list: () => buildEndpoint(RECOVERABLE_EXPENSE_BASE),
  recoveryTargets: () => buildEndpoint(`${RECOVERABLE_EXPENSE_BASE}/recovery-targets`),
  getById: (id: ID) => buildEndpointWithParams(`${RECOVERABLE_EXPENSE_BASE}/:id`, { id }),
  create: () => buildEndpoint(RECOVERABLE_EXPENSE_BASE),
  cancel: (id: ID) => buildEndpointWithParams(`${RECOVERABLE_EXPENSE_BASE}/:id`, { id }),
};

const CUSTOMER_GROUP_BASE = '/customer-groups';
export const customerGroupEndpoints = {
  list: () => buildEndpoint(CUSTOMER_GROUP_BASE),
  lookup: () => buildEndpoint(`${CUSTOMER_GROUP_BASE}/lookup`),
  getById: (id: ID) => buildEndpointWithParams(`${CUSTOMER_GROUP_BASE}/:id`, { id }),
  create: () => buildEndpoint(CUSTOMER_GROUP_BASE),
  update: (id: ID) => buildEndpointWithParams(`${CUSTOMER_GROUP_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${CUSTOMER_GROUP_BASE}/:id`, { id }),
  validateCode: (code: string) => buildEndpointWithParams(`${CUSTOMER_GROUP_BASE}/validate/code/:code`, { code }),
};

const SERVICE_CATEGORY_BASE = '/service-categories';
export const serviceCategoryEndpoints = {
  list: () => buildEndpoint(SERVICE_CATEGORY_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${SERVICE_CATEGORY_BASE}/:id`, { id }),
  create: () => buildEndpoint(SERVICE_CATEGORY_BASE),
  update: (id: ID) => buildEndpointWithParams(`${SERVICE_CATEGORY_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${SERVICE_CATEGORY_BASE}/:id`, { id }),
  validateCode: (code: string) => buildEndpointWithParams(`${SERVICE_CATEGORY_BASE}/validate/code/:code`, { code }),
} as const;

const SALES_INVOICE_BASE = '/sales-invoices';
export const salesInvoiceEndpoints = {
  list: () => buildEndpoint(SALES_INVOICE_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${SALES_INVOICE_BASE}/:id`, { id }),
  create: () => buildEndpoint(SALES_INVOICE_BASE),
  update: (id: ID) => buildEndpointWithParams(`${SALES_INVOICE_BASE}/:id`, { id }),
  remove: (id: ID) => buildEndpointWithParams(`${SALES_INVOICE_BASE}/:id`, { id }),
  cancel: (id: ID) => buildEndpointWithParams(`${SALES_INVOICE_BASE}/:id/cancel`, { id }),
  /** The income ledger each item line posts to on the invoice date (ADR-0058 §8). */
  lineLedgers: () => buildEndpoint(`${SALES_INVOICE_BASE}/line-ledgers`),
  /** The income ledger each picked service posts to (repeat `serviceIds`). */
  serviceLedgers: () => buildEndpoint(`${SALES_INVOICE_BASE}/service-ledgers`),
  /** An item's base-unit quantity at a location on a date — the item line's on-hand hint. */
  stockOnHand: () => buildEndpoint(`${SALES_INVOICE_BASE}/stock-on-hand`),
} as const;

const CREDIT_NOTE_BASE = '/credit-notes';
export const creditNoteEndpoints = {
  list: () => buildEndpoint(CREDIT_NOTE_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${CREDIT_NOTE_BASE}/:id`, { id }),
  create: () => buildEndpoint(CREDIT_NOTE_BASE),
  update: (id: ID) => buildEndpointWithParams(`${CREDIT_NOTE_BASE}/:id`, { id }),
  remove: (id: ID) => buildEndpointWithParams(`${CREDIT_NOTE_BASE}/:id`, { id }),
  cancel: (id: ID) => buildEndpointWithParams(`${CREDIT_NOTE_BASE}/:id/cancel`, { id }),
  invoicePrefill: (invoiceId: ID) => buildEndpointWithParams(`${CREDIT_NOTE_BASE}/invoice-prefill/:invoiceId`, { invoiceId }),
  /** Credit notes + Sales Debit Notes in one register (ADR-0053). */
  register: () => buildEndpoint(`${CREDIT_NOTE_BASE}/register`),
  /** The income ledger each item line reverses on the note date (ADR-0058 §8). */
  lineLedgers: () => buildEndpoint(`${CREDIT_NOTE_BASE}/line-ledgers`),
  /** The income ledger each picked service reverses (repeat `serviceIds`). */
  serviceLedgers: () => buildEndpoint(`${CREDIT_NOTE_BASE}/service-ledgers`),
} as const;

const PURCHASE_INVOICE_BASE = '/purchase-invoices';
export const purchaseInvoiceEndpoints = {
  list: () => buildEndpoint(PURCHASE_INVOICE_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${PURCHASE_INVOICE_BASE}/:id`, { id }),
  create: () => buildEndpoint(PURCHASE_INVOICE_BASE),
  update: (id: ID) => buildEndpointWithParams(`${PURCHASE_INVOICE_BASE}/:id`, { id }),
  remove: (id: ID) => buildEndpointWithParams(`${PURCHASE_INVOICE_BASE}/:id`, { id }),
  cancel: (id: ID) => buildEndpointWithParams(`${PURCHASE_INVOICE_BASE}/:id/cancel`, { id }),
  duplicateCheck: () => buildEndpoint(`${PURCHASE_INVOICE_BASE}/duplicate-check`),
  itcRevision: (id: ID) => buildEndpointWithParams(`${PURCHASE_INVOICE_BASE}/:id/itc-revision`, { id }),
  details: (id: ID) => buildEndpointWithParams(`${PURCHASE_INVOICE_BASE}/:id/details`, { id }),
  /** The engine's tax for the item lines — a pre-fill, never binding. */
  taxPreview: () => buildEndpoint(`${PURCHASE_INVOICE_BASE}/tax-preview`),
  /** The ledger each item line posts to on the booking date. */
  lineLedgers: () => buildEndpoint(`${PURCHASE_INVOICE_BASE}/line-ledgers`),
};

// ADR-0049 Slice 2
const PURCHASE_DEBIT_NOTE_BASE = '/purchase-debit-notes';
export const purchaseDebitNoteEndpoints = {
  list: () => buildEndpoint(PURCHASE_DEBIT_NOTE_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${PURCHASE_DEBIT_NOTE_BASE}/:id`, { id }),
  prefill: (invoiceId: ID) => buildEndpointWithParams(`${PURCHASE_DEBIT_NOTE_BASE}/prefill/:invoiceId`, { invoiceId }),
  create: () => buildEndpoint(PURCHASE_DEBIT_NOTE_BASE),
  update: (id: ID) => buildEndpointWithParams(`${PURCHASE_DEBIT_NOTE_BASE}/:id`, { id }),
  remove: (id: ID) => buildEndpointWithParams(`${PURCHASE_DEBIT_NOTE_BASE}/:id`, { id }),
  duplicateCheck: () => buildEndpoint(`${PURCHASE_DEBIT_NOTE_BASE}/duplicate-check`),
  /** Our debit notes + Supplier Debit Notes in one register (ADR-0053). */
  register: () => buildEndpoint(`${PURCHASE_DEBIT_NOTE_BASE}/register`),
  /** The ledger each returned item line credits on the note date. */
  lineLedgers: () => buildEndpoint(`${PURCHASE_DEBIT_NOTE_BASE}/line-ledgers`),
};

// ADR-0051 — open-item set-off, supplier (AP) face
const SUPPLIER_SETOFF_BASE = '/supplier-setoffs';
export const supplierSetoffEndpoints = {
  list: () => buildEndpoint(SUPPLIER_SETOFF_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${SUPPLIER_SETOFF_BASE}/:id`, { id }),
  candidates: () => buildEndpoint(`${SUPPLIER_SETOFF_BASE}/candidates`),
  create: () => buildEndpoint(SUPPLIER_SETOFF_BASE),
  cancel: (id: ID) => buildEndpointWithParams(`${SUPPLIER_SETOFF_BASE}/:id/cancel`, { id }),
  markDeduction: () => buildEndpoint(`${SUPPLIER_SETOFF_BASE}/mark-deduction`),
  deductionReasons: () => buildEndpoint(`${SUPPLIER_SETOFF_BASE}/deduction-reasons`),
} as const;

/** Customer Set-off (ADR-0051 AR face, ADR-0060 slice 3) — same shape as the supplier's. */
const CUSTOMER_SETOFF_BASE = '/customer-setoffs';
export const customerSetoffEndpoints = {
  list: () => buildEndpoint(CUSTOMER_SETOFF_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${CUSTOMER_SETOFF_BASE}/:id`, { id }),
  candidates: () => buildEndpoint(`${CUSTOMER_SETOFF_BASE}/candidates`),
  create: () => buildEndpoint(CUSTOMER_SETOFF_BASE),
  cancel: (id: ID) => buildEndpointWithParams(`${CUSTOMER_SETOFF_BASE}/:id/cancel`, { id }),
  markDeduction: () => buildEndpoint(`${CUSTOMER_SETOFF_BASE}/mark-deduction`),
  deductionReasons: () => buildEndpoint(`${CUSTOMER_SETOFF_BASE}/deduction-reasons`),
} as const;

const VOUCHER_NUMBER_BASE = '/voucher-numbers';
export const voucherNumberEndpoints = {
  preview: () => buildEndpoint(`${VOUCHER_NUMBER_BASE}/preview`),
} as const;

const SERVICE_BASE = '/services';
export const serviceEndpoints = {
  list: () => buildEndpoint(SERVICE_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${SERVICE_BASE}/:id`, { id }),
  create: () => buildEndpoint(SERVICE_BASE),
  update: (id: ID) => buildEndpointWithParams(`${SERVICE_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${SERVICE_BASE}/:id`, { id }),
  validateCode: (code: string) => buildEndpointWithParams(`${SERVICE_BASE}/validate/code/:code`, { code }),
} as const;
